import { useCallback, useEffect, useMemo, useState } from 'react';
import Badge from '../Badge/Badge';
import axiosInstance from '../../reactWrapper/endpoints/axios';

/**
 * What the client asked for, grouped by the role they asked it under.
 *
 * Specialties and tools are both picked per role — a second role usually
 * sits in another category and needs entirely different ones. Laying them
 * out as flat lists under one heading made the reader guess which belonged
 * to which, and with two roles that guess is wrong half the time. Each role
 * gets its own block instead.
 *
 * Skills stay job-wide: they are asked of whoever takes the job rather than
 * of one of its roles, which is how the wizard collects them.
 *
 * Jobs posted before the wizard have no attribution, so they fall back to
 * `stack` and the numbered columns, resolved one id at a time.
 */
const SkillsInfo = ({ SkillsInfo: job }) => {
  const taxonomy = job?.taxonomy ?? null;
  const hasTaxonomy = !!(taxonomy && taxonomy.roles && taxonomy.roles.length);

  const [languages, setLanguages] = useState([]);
  const [skills, setSkills] = useState([]);
  const [frameworks, setFrameworks] = useState([]);

  const fetchRealNames = useCallback(async () => {
    if (!job || hasTaxonomy) return;
    const pull = async (prefix, kind, setter) => {
      const out = [];
      for (let idx = 1; idx <= 20; idx++) {
        const id = job[prefix + idx];
        if (!id) break;
        try {
          const res = await axiosInstance.get(`/all/getCbtParamsRealname/${id}/${kind}`);
          out.push(res.data.data);
        } catch {}
      }
      setter(out);
    };
    await pull('language', 'language', setLanguages);
    await pull('skill', 'skill', setSkills);
    await pull('framework', 'framework', setFrameworks);
  }, [job, hasTaxonomy]);

  useEffect(() => {
    fetchRealNames();
  }, [fetchRealNames]);

  const roles = useMemo(() => {
    if (hasTaxonomy) return taxonomy.roles;
    return job?.stack
      ? [{ kind: 'primary', id: 'legacy', name: job.stack, specialties: [], tools: [] }]
      : [];
  }, [hasTaxonomy, taxonomy, job]);

  const skillNames = hasTaxonomy ? taxonomy.skills.map((s) => s.name) : skills;
  /* Picked before the wizard recorded which role they belonged to. */
  const looseSpecialties = hasTaxonomy
    ? (taxonomy.unassigned_specialties ?? []).map((s) => s.name)
    : languages;
  const looseTools = hasTaxonomy ? (taxonomy.unassigned_tools ?? []).map((t) => t.name) : frameworks;

  const Empty = () => <span className='text-gray-400'>N/A</span>;

  const ChipRow = ({ label, items }) =>
    items.length ? (
      <div className='mt-2'>
        <p className='text-[11px] uppercase tracking-wide text-muted-foreground mb-1'>{label}</p>
        <div className='flex gap-2 flex-wrap'>
          {items.map((name, i) => (
            <Badge key={i} label={name} />
          ))}
        </div>
      </div>
    ) : null;

  return (
    <div className='space-y-5'>
      <div>
        <h3 className='font-semibold leading-8 lg:text-xl mb-3'>What this job asks for</h3>
        {roles.length === 0 ? (
          <Empty />
        ) : (
          <div className='space-y-3'>
            {roles.map((role) => (
              <div key={role.id} className='rounded-xl border bg-background p-4'>
                <div className='flex items-center gap-2 flex-wrap'>
                  <span className='text-[11px] uppercase tracking-wide text-muted-foreground'>
                    {role.kind === 'secondary' ? 'Secondary role' : 'Primary role'}
                  </span>
                  {role.kind === 'secondary' && (
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
                        role.required
                          ? 'bg-amber-50 text-amber-800 border-amber-200'
                          : 'bg-slate-50 text-slate-600 border-slate-200'
                      }`}
                    >
                      {role.required ? 'Must have both' : 'Nice to have'}
                    </span>
                  )}
                </div>
                <p className='font-semibold text-base mt-1'>{role.name}</p>
                <ChipRow label='Specialties' items={(role.specialties ?? []).map((s) => s.name)} />
                <ChipRow label='Tools' items={(role.tools ?? []).map((t) => t.name)} />
              </div>
            ))}
          </div>
        )}
      </div>

      {(looseSpecialties.length > 0 || looseTools.length > 0) && (
        <div className='rounded-xl border bg-background p-4'>
          <p className='text-[11px] uppercase tracking-wide text-muted-foreground'>
            Not tied to a role
          </p>
          <ChipRow label='Specialties' items={looseSpecialties} />
          <ChipRow label='Tools' items={looseTools} />
        </div>
      )}

      <div>
        <h3 className='font-semibold leading-8 lg:text-xl mb-3'>Skill(s)</h3>
        <div className='flex gap-2 flex-wrap'>
          {skillNames.map((name, i) => (
            <Badge key={i} label={name} />
          ))}
          {skillNames.length === 0 && <Empty />}
        </div>
      </div>
    </div>
  );
};

export default SkillsInfo;
